import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Tab,
  Tabs,
} from '@structural-climbing/ui'
import {
  competitionBoards,
  disciplineForSection,
  type CompetitionDiscipline,
  type Discipline,
} from '@structural-climbing/validation'
import { AddRouteModal } from '../routes/AddRouteModal'
import { EditRouteModal } from '../routes/EditRouteModal'
import type { GymSection } from '../routes/types'
import { CompetitionRouteDetailModal } from './CompetitionRouteDetailModal'
import { RouteIdentity } from './RouteIdentity'
import { RoutePickerModal } from './RoutePickerModal'
import {
  competitionState,
  routeDiscipline,
  type CompetitionResult,
  type CompetitionRound,
  type CompetitionRouteRef,
  type CompetitionState,
} from './types'

interface Props {
  competitionId: string
  discipline: CompetitionDiscipline
  routes: CompetitionRouteRef[]
  /**
   * Empty for a competition that does not use rounds; then this tab is
   * unchanged. Undefined while a deploy is mid-flight and the Worker still
   * predates rounds, so it is tolerated rather than assumed.
   */
  rounds: CompetitionRound[] | undefined
  /** Gym-local wall clock, for deciding which round is live right now. */
  localNow: string
  /** Sections for this competition's discipline, for the new-route form. */
  sections: GymSection[]
  /** The competition is over, so its routes are the result and cannot change. */
  locked: boolean
  /** For the route detail's top count; without them the count is left out. */
  results?: CompetitionResult[]
  onRefresh: () => void
  /** Told whenever staged round or number changes appear or go away. */
  onDirtyChange?: (dirty: boolean) => void
}

/** The key the Select uses for "not in a round", since a Key cannot be null. */
const NO_ROUND = 'all'

const STATE_STYLES: Record<CompetitionState, string> = {
  upcoming: 'bg-default/40 text-foreground/60',
  in_progress: 'bg-success/15 text-success-700',
  ended: 'bg-default/40 text-foreground/60',
}

const STATE_KEYS: Record<CompetitionState, string> = {
  upcoming: 'competitions.round_upcoming',
  in_progress: 'competitions.round_live',
  ended: 'competitions.round_ended',
}

export function RoutesTab({
  competitionId,
  discipline,
  routes,
  rounds: givenRounds,
  localNow,
  sections,
  locked,
  results,
  onRefresh,
  onDirtyChange,
}: Props) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const rounds = givenRounds ?? []

  const [pickerOpen, setPickerOpen] = useState(false)
  // Which wall the new-route form is for; null while it is closed. A combined
  // competition sets routes on both, and the form needs one grade scale.
  const [addDiscipline, setAddDiscipline] = useState<Discipline | null>(null)
  const boards = competitionBoards(discipline)
  // The wall on show in a combined competition. Thirty boulders above the lead
  // routes is a long scroll, so each is one tab away.
  const [board, setBoard] = useState<Discipline>(boards[0])
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [roundEdits, setRoundEdits] = useState<Record<string, string | null>>({})
  const [detailRef, setDetailRef] = useState<CompetitionRouteRef | null>(null)
  const [editRef, setEditRef] = useState<CompetitionRouteRef | null>(null)
  const [removeRef, setRemoveRef] = useState<CompetitionRouteRef | null>(null)
  const [removing, setRemoving] = useState(false)
  const [saving, setSaving] = useState(false)

  const combined = boards.length > 1
  const disciplineOf = (ref: CompetitionRouteRef) =>
    routeDiscipline(discipline, ref.route?.section_id, sections)

  // Position within its discipline is the fallback label, so a route that
  // reached the competition without one still reads the way the wall is
  // marked: bouldering 1..n, lead 1..n.
  const fallbackLabels = new Map<string, string>()
  const seen: Partial<Record<Discipline, number>> = {}
  for (const ref of routes) {
    const d = disciplineOf(ref)
    seen[d] = (seen[d] ?? 0) + 1
    fallbackLabels.set(ref.route_id, String(seen[d]))
  }
  const labelFor = (ref: CompetitionRouteRef) =>
    labels[ref.route_id] ?? ref.label ?? fallbackLabels.get(ref.route_id) ?? ''

  // What the new-route form suggests: one past the highest number already on
  // that wall, the same rule the Worker applies when no number is sent.
  const addLabels = addDiscipline
    ? routes.filter((ref) => disciplineOf(ref) === addDiscipline).map(labelFor)
    : []
  const nextNumber =
    Math.max(
      addLabels.length,
      ...addLabels.map((l) => Number.parseInt(l, 10)).filter(Number.isFinite),
    ) + 1

  const roundFor = (ref: CompetitionRouteRef) =>
    ref.route_id in roundEdits ? roundEdits[ref.route_id] : ref.round_id

  // Numbers restart per discipline in a combined competition, so "1" alone
  // would not say which wall.
  const titleFor = (ref: CompetitionRouteRef) =>
    combined
      ? `${t(`competitions.discipline_${disciplineOf(ref)}`)} · ${labelFor(ref)}`
      : labelFor(ref)

  const dirty = Object.keys(labels).length > 0 || Object.keys(roundEdits).length > 0

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  // A refresh with staged changes is how a round move got lost: the save bar
  // was below the fold. The browser's own prompt covers refresh and close.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  function discard() {
    setLabels({})
    setRoundEdits({})
  }

  async function save() {
    setSaving(true)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/competitions/${competitionId}/routes`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          routes: routes.map((ref, index) => ({
            route_id: ref.route_id,
            label: labelFor(ref),
            sort_order: index,
            round_id: roundFor(ref),
          })),
        }),
      })
      // Keep the edits on a failure, so the bar stays up and nothing looks
      // saved that was not.
      if (!res.ok) {
        addToast({ title: t('competitions.routeChangesFailed'), color: 'danger' })
        return
      }
      addToast({ title: t('competitions.routeChangesSaved'), color: 'success' })
      discard()
      onRefresh()
    } catch {
      addToast({ title: t('competitions.routeChangesFailed'), color: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  async function removeRoute(routeId: string) {
    const token = await getAccessTokenSilently()
    await fetch(`${apiUrl}/competitions/${competitionId}/routes/${routeId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    onRefresh()
  }

  const topsOn = (ref: CompetitionRouteRef) =>
    results?.filter((r) => r.route_id === ref.route_id).length ?? 0

  // A wall route can be picked again, so removing it is one press. Removing
  // one that climbers have topped, or one set for this competition, is not so
  // easily undone: the tops stay but the route drops out of the competition,
  // and a competition route cannot be picked back from the wall. Ask first.
  function requestRemove(ref: CompetitionRouteRef) {
    if (topsOn(ref) > 0 || ref.route?.competition_only) setRemoveRef(ref)
    else void removeRoute(ref.route_id)
  }

  async function confirmRemove() {
    if (!removeRef) return
    setRemoving(true)
    try {
      await removeRoute(removeRef.route_id)
      setRemoveRef(null)
    } finally {
      setRemoving(false)
    }
  }

  // Keep every route's position in the flat list, because that position is its
  // sort_order on save. Grouping only changes what the gym reads, never what
  // gets sent.
  const indexed = routes.map((ref, index) => ({ ref, index }))
  type Item = (typeof indexed)[number]

  const roundGroups = (items: Item[]) =>
    rounds.length
      ? [
          ...rounds.map((round) => ({
            key: round.id,
            name: round.name,
            state: competitionState(round, localNow) as CompetitionState | null,
            items: items.filter(({ ref }) => roundFor(ref) === round.id),
          })),
          {
            key: NO_ROUND,
            name: t('competitions.roundAll'),
            state: null,
            items: items.filter(({ ref }) => roundFor(ref) === null),
          },
        ]
      : [{ key: NO_ROUND, name: '', state: null, items }]

  function row({ ref }: Item) {
    return (
      <li
        key={ref.id}
        className="flex flex-wrap items-center gap-2 rounded-lg border border-divider bg-content1 p-2 sm:flex-nowrap sm:gap-3"
      >
        <Input
          size="sm"
          aria-label={t('competitions.routeLabel')}
          className="w-20 flex-none"
          isReadOnly={locked}
          value={labelFor(ref)}
          onValueChange={(v) => setLabels((prev) => ({ ...prev, [ref.route_id]: v }))}
        />

        {ref.route && (
          // Fills the row between the number and the controls, so pressing the
          // row opens the route while typing a number does not.
          <Button
            variant="light"
            className="h-auto min-w-0 flex-1 justify-start gap-2 whitespace-normal p-1 text-left sm:gap-3"
            aria-label={t('competitions.viewRouteDetails', { label: titleFor(ref) })}
            onPress={() => setDetailRef(ref)}
          >
            <RouteIdentity
              route={ref.route}
              apiUrl={apiUrl}
              discipline={routeDiscipline(discipline, ref.route.section_id, sections)}
            />
          </Button>
        )}

        {/* A line of their own on a phone: beside the route they would squeeze
            its photo down to a sliver. */}
        <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:ml-auto sm:w-auto sm:flex-none sm:flex-nowrap">
          {rounds.length > 0 && !locked && (
            <Select
              size="sm"
              aria-label={t('competitions.roundAssign')}
              className="w-40"
              selectedKeys={[roundFor(ref) ?? NO_ROUND]}
              onSelectionChange={(keys) => {
                const key = String([...keys][0] ?? NO_ROUND)
                setRoundEdits((prev) => ({
                  ...prev,
                  [ref.route_id]: key === NO_ROUND ? null : key,
                }))
              }}
            >
              {[
                <SelectItem key={NO_ROUND} textValue={t('competitions.roundAll')}>
                  {t('competitions.roundAll')}
                </SelectItem>,
                ...rounds.map((round) => (
                  <SelectItem key={round.id} textValue={round.name}>
                    {round.name}
                  </SelectItem>
                )),
              ]}
            </Select>
          )}
          {ref.route?.competition_only && (
            <Chip size="sm" variant="flat" color="warning">
              {t('competitions.competitionOnlyBadge')}
            </Chip>
          )}
          {!locked && (
            <Button size="sm" variant="light" color="danger" onPress={() => requestRemove(ref)}>
              {t('common.remove')}
            </Button>
          )}
        </div>
      </li>
    )
  }

  function renderGroups(groups: ReturnType<typeof roundGroups>) {
    return (
      groups
        // A round with no routes yet still shows, so the gap is visible; the
        // catch-all group only shows when something is actually in it.
        .filter((group) => group.items.length > 0 || group.key !== NO_ROUND)
        .map((group) => (
          <div key={group.key} className="flex flex-col gap-2">
            {group.name && (
              <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                {group.name}
                {group.state && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[group.state]}`}
                  >
                    {t(STATE_KEYS[group.state])}
                  </span>
                )}
              </h3>
            )}
            {group.items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-default-200 p-3 text-sm text-foreground/50">
                {t('competitions.roundNoRoutes')}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">{group.items.map(row)}</ul>
            )}
          </div>
        ))
    )
  }

  const boardItems = indexed.filter(({ ref }) => disciplineOf(ref) === board)

  return (
    <div className="flex flex-col gap-4">
      {/* Changing the route set after the end would rewrite standings that
          competitors have already seen. */}
      {locked ? (
        <p className="rounded-lg bg-content2 p-3 text-sm text-foreground/60">
          {t('competitions.endedLockNote')}
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            <Button color="primary" size="sm" onPress={() => setPickerOpen(true)}>
              {t('competitions.pickRoutes')}
            </Button>
            {/* A combined competition adds routes from inside each section, so
                a route always lands where the gym is looking. */}
            {!combined && (
              <Button variant="bordered" size="sm" onPress={() => setAddDiscipline(boards[0])}>
                {t('competitions.addCompetitionRoute')}
              </Button>
            )}
          </div>
          <p className="text-sm text-foreground/60">
            {t('competitions.routesHint')}
            {rounds.length > 0 && ` ${t('competitions.routesRoundHint')}`}
          </p>
        </>
      )}

      {combined ? (
        // Bouldering and lead are scored on separate boards, so they are set,
        // numbered and read as two lists, one tab each.
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tabs
              aria-label={t('routes.sectionDiscipline')}
              selectedKey={board}
              onSelectionChange={(key) => setBoard(key as Discipline)}
            >
              {boards.map((b) => (
                <Tab
                  key={b}
                  title={
                    <span className="flex items-center gap-2">
                      {t(`competitions.discipline_${b}`)}
                      <span className="rounded-full bg-default-100 px-2 py-0.5 text-xs font-medium text-foreground/60">
                        {indexed.filter(({ ref }) => disciplineOf(ref) === b).length}
                      </span>
                    </span>
                  }
                />
              ))}
            </Tabs>
            {!locked && (
              <Button size="sm" variant="bordered" onPress={() => setAddDiscipline(board)}>
                {t(`competitions.addCompetitionRoute_${board}`)}
              </Button>
            )}
          </div>
          {boardItems.length === 0 ? (
            <p className="rounded-lg border border-dashed border-default-200 p-4 text-center text-sm text-foreground/50">
              {t('competitions.boardNoRoutes')}
            </p>
          ) : (
            renderGroups(roundGroups(boardItems))
          )}
        </>
      ) : routes.length === 0 ? (
        <Card className="shadow-sm">
          <CardBody className="py-8 text-center text-foreground/60">
            {t('competitions.routesEmpty')}
          </CardBody>
        </Card>
      ) : (
        renderGroups(roundGroups(indexed))
      )}

      {/* Pinned to the bottom of the screen: the round a route is moved into
          is often further down the list than the top of the tab. */}
      {dirty && !locked && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-divider bg-content1 p-3 shadow-lg">
          <p className="text-sm font-medium">{t('competitions.unsavedRouteChanges')}</p>
          <div className="flex gap-2">
            <Button size="sm" variant="light" isDisabled={saving} onPress={discard}>
              {t('competitions.discardChanges')}
            </Button>
            {/* Saves round assignments as well as labels. */}
            <Button color="success" size="sm" isLoading={saving} onPress={() => void save()}>
              {t('competitions.saveRouteChanges')}
            </Button>
          </div>
        </div>
      )}

      <RoutePickerModal
        isOpen={pickerOpen}
        competitionId={competitionId}
        discipline={discipline}
        sections={sections}
        current={routes}
        onClose={() => setPickerOpen(false)}
        onSaved={onRefresh}
      />
      <CompetitionRouteDetailModal
        route={detailRef?.route ?? null}
        title={detailRef ? titleFor(detailRef) : ''}
        discipline={detailRef ? disciplineOf(detailRef) : boards[0]}
        sectionName={sections.find((s) => s.id === detailRef?.route?.section_id)?.name}
        roundName={rounds.find((r) => r.id === (detailRef ? roundFor(detailRef) : null))?.name}
        topCount={
          results && detailRef
            ? results.filter((r) => r.route_id === detailRef.route_id).length
            : undefined
        }
        apiUrl={apiUrl}
        onEdit={
          locked
            ? undefined
            : () => {
                setEditRef(detailRef)
                setDetailRef(null)
              }
        }
        onClose={() => setDetailRef(null)}
      />
      <EditRouteModal
        route={
          editRef?.route
            ? {
                ...editRef.route,
                section: editRef.route.section_id ? { id: editRef.route.section_id } : null,
              }
            : null
        }
        apiUrl={apiUrl}
        // Only the sections on the route's own wall: moving it to the other
        // one would put it on the other board under a number that may be taken.
        sections={
          editRef
            ? sections.filter((s) => disciplineForSection(s.discipline) === disciplineOf(editRef))
            : []
        }
        discipline={editRef ? disciplineOf(editRef) : boards[0]}
        graded={editRef?.route?.grade != null}
        onClose={() => setEditRef(null)}
        onSaved={onRefresh}
      />
      <Modal scrollBehavior="inside" isOpen={removeRef !== null} onClose={() => setRemoveRef(null)}>
        <ModalContent>
          <ModalHeader>
            {t('competitions.removeRouteTitle', { label: removeRef ? titleFor(removeRef) : '' })}
          </ModalHeader>
          <ModalBody className="gap-2 text-sm">
            {removeRef && topsOn(removeRef) > 0 && (
              <p>{t('competitions.removeRouteTops', { count: topsOn(removeRef) })}</p>
            )}
            {removeRef?.route?.competition_only && (
              <p>{t('competitions.removeRouteCompetitionOnly')}</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={() => setRemoveRef(null)}>
              {t('common.cancel')}
            </Button>
            <Button color="danger" isLoading={removing} onPress={() => void confirmRemove()}>
              {t('common.remove')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {addDiscipline && (
        <AddRouteModal
          isOpen
          apiUrl={apiUrl}
          sections={sections.filter((s) => disciplineForSection(s.discipline) === addDiscipline)}
          discipline={addDiscipline}
          competitionId={competitionId}
          suggestedNumber={String(nextNumber)}
          takenNumbers={addLabels}
          onClose={() => setAddDiscipline(null)}
          onCreated={onRefresh}
        />
      )}
    </div>
  )
}
